import React, { useEffect, useState } from "react"
import { motion, useMotionValue, useTransform } from "framer-motion"
import { Sparkles, Stars } from "lucide-react"
import serverName from "../serverName"
import logo from "../logo.svg"
import { Link } from "react-router-dom"
import { apiGet } from "../api"

const IMAGES = [
    "https://images.unsplash.com/photo-1511512578047-dfb367046420?q=80&w=1880&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1511512578047-7b65499bd56d?q=80&w=1880&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?q=80&w=1880&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=1880&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1520975922322-2609e99a6f2e?q=80&w=1880&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1520975682030-26f0d98dcd1b?q=80&w=1880&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1482192505345-5655af888cc4?q=80&w=1880&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1535223289827-42f1e9919769?q=80&w=1880&auto=format&fit=crop",
]

const SHARDS = [
    { i: 0, p: "5% 5%, 35% 3%, 30% 25%, 8% 28%" },
    { i: 1, p: "36% 3%, 65% 6%, 58% 26%, 30% 25%" },
    { i: 2, p: "66% 6%, 95% 10%, 90% 30%, 58% 26%" },
    { i: 3, p: "8% 28%, 30% 25%, 26% 55%, 6% 60%" },
    { i: 4, p: "30% 25%, 58% 26%, 54% 56%, 26% 55%" },
    { i: 5, p: "58% 26%, 90% 30%, 88% 58%, 54% 56%" },
    { i: 6, p: "6% 60%, 26% 55%, 23% 85%, 4% 88%" },
    { i: 7, p: "26% 55%, 54% 56%, 51% 88%, 23% 85%" },
    { i: 0, p: "54% 56%, 88% 58%, 95% 88%, 51% 88%" },
]

function Starfield() {
    return (
        <div className="pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,rgba(0,0,0,1),rgba(0,0,0,0.15))]">
            <div className="absolute inset-0 opacity-50" style={{ backgroundImage: "radial-gradient(1px 1px at 20% 30%,rgba(255,255,255,.6),transparent), radial-gradient(1px 1px at 80% 60%,rgba(255,255,255,.5),transparent), radial-gradient(1px 1px at 50% 80%,rgba(255,255,255,.4),transparent)", backgroundRepeat: "no-repeat" }} />
            <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_50%_-20%,rgba(124,58,237,.25),transparent)]" />
            <div className="absolute inset-0 bg-[radial-gradient(800px_400px_at_80%_20%,rgba(59,130,246,.2),transparent)]" />
            <div className="absolute inset-0 bg-[radial-gradient(1000px_500px_at_20%_80%,rgba(16,185,129,.18),transparent)]" />
            <div className="absolute inset-0 opacity-30 mix-blend-soft-light bg-[linear-gradient(120deg,rgba(255,255,255,.05)_0%,rgba(255,255,255,0)_60%)] animate-[pulse_6s_infinite]" />
            <div className="absolute inset-0 [background:linear-gradient(rgba(255,255,255,.06),rgba(255,255,255,.02))] [mask-image:linear-gradient(to_bottom,transparent,black_15%,black_85%,transparent)]" />
            <div className="absolute inset-0 bg-[linear-gradient(transparent_0,rgba(255,255,255,.04)_1px,transparent_1px),linear-gradient(90deg,transparent_0,rgba(255,255,255,.04)_1px,transparent_1px)] bg-[size:24px_24px] opacity-10" />
        </div>
    )
}

function Noise() {
    return (
        <div
            className="pointer-events-none absolute inset-0 opacity-20 mix-blend-overlay"
            style={{
                backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='800' height='600'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.2'/></svg>")`
            }}
        />
    )
}

function GlowButton({ children }) {
    return (
        <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }} className="relative inline-flex items-center gap-2 rounded-2xl px-6 py-3 font-semibold tracking-wide text-white shadow-xl shadow-purple-900/20 ring-1 ring-white/10 backdrop-blur bg-gradient-to-br from-purple-600 via-fuchsia-600 to-indigo-600">
            <span className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/20 to-white/0 opacity-20" />
            <span className="relative z-10 flex items-center gap-2">{children}</span>
        </motion.button>
    )
}

function ShardGallery() {
    const rotate = useMotionValue(0)
    const r = useTransform(rotate, [0, 1], ["0deg", "1deg"])
    return (
        <div className="relative mx-auto mt-12 w-full max-w-5xl aspect-[16/9] [perspective:1200px]">
            <motion.div style={{ rotateX: r }} onMouseMove={(e)=>rotate.set(1)} onMouseLeave={()=>rotate.set(0)} className="absolute inset-0 rounded-3xl overflow-hidden ring-1 ring-white/10 bg-black/30 backdrop-blur">
                {SHARDS.map((s, idx)=>{
                    const img = IMAGES[(s.i + idx) % IMAGES.length]
                    const delay = idx * 0.08
                    return (
                        <motion.img key={idx} src={img} alt="shot" initial={{ y: 0, opacity: .9 }} animate={{ y: [0, -6, 0] }} transition={{ duration: 6 + idx*.15, delay, repeat: Infinity, ease: "easeInOut" }} style={{ clipPath: `polygon(${s.p})` }} className="absolute inset-0 h-full w-full object-cover will-change-transform" />
                    )
                })}
                <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/40" />
            </motion.div>
            <div className="absolute -inset-1 rounded-3xl blur-xl bg-gradient-to-tr from-fuchsia-600/30 via-purple-500/20 to-indigo-500/20" />
        </div>
    )
}

function Stat({ k, v }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur text-left">
            <div className="text-xs uppercase tracking-widest text-white/50">{k}</div>
            <div className="text-2xl font-bold leading-tight">{v}</div>
        </div>
    )
}

function Marquee() {
    return (
        <div className="relative mt-14 overflow-hidden">
            <div className="absolute inset-0 pointer-events-none [mask-image:linear-gradient(to_right,transparent,black_15%,black_85%,transparent)]" />
            <motion.div animate={{ x: [0, -1200] }} transition={{ duration: 24, repeat: Infinity, ease: "linear" }} className="flex gap-6 whitespace-nowrap">
                {["Roleplay","Whitelist","Ekonomika","Eventy","Skvělá komunita","Stabilní servery","Aktivní admin","Věrná mapa","Custom vozidla","Módy","Soutěže","Daily odměny"].map((t,i)=> (
                    <span key={i} className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm backdrop-blur">{t}</span>
                ))}
            </motion.div>
        </div>
    )
}

function useServerStats() {
    const [stats, setStats] = useState({ online: false, players: 0, max: 0, whitelist: false, join: null })
    const [loading, setLoading] = useState(true)
    const load = async () => {
        try {
            const res = await apiGet(`/server_status.php?ts=${Date.now()}`)
            const join = res?.join || res?.connect || res?.cfx || null
            setStats({
                online: !!res?.online,
                players: Number(res?.players) || 0,
                max: Number(res?.max) || 0,
                whitelist: !!res?.whitelist,
                join
            })
        } catch {
            setStats(s => ({ ...s, online: false }))
        } finally {
            setLoading(false)
        }
    }
    useEffect(() => {
        load()
        const t = setInterval(load, 15000)
        return () => clearInterval(t)
    }, [])
    return { stats, loading, reload: load }
}

export default function Home() {
    const { stats, loading } = useServerStats()

    return (
        <section className="relative w-full overflow-hidden text-white pt-28 pb-20">
            <Starfield />
            <Noise />
            <div className="absolute inset-x-0 -top-40 h-80 blur-3xl" aria-hidden>
                <div className="mx-auto h-full w-[80%] bg-gradient-to-r from-fuchsia-600/40 via-purple-600/40 to-indigo-600/40" />
            </div>

            <div className="relative mx-auto max-w-7xl px-4 pt-28 pb-20">
                <div className="mx-auto max-w-3xl text-center">
                    <img src={logo} alt="Amnesia logo" className="mx-auto mb-6 h-24 w-24" />
                    <div className="relative inline-block">
                        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .8 }} className="bg-gradient-to-b from-white to-white/70 bg-clip-text text-transparent text-5xl sm:text-6xl font-extrabold tracking-tight">
                            {serverName} RP
                        </motion.h1>
                        <span className="pointer-events-none absolute -inset-1 -z-10 blur-xl bg-gradient-to-r from-purple-500/30 via-fuchsia-500/30 to-indigo-500/30" />
                    </div>
                    <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .2, duration: .7 }} className="mt-4 text-lg text-white/70">
                        Moderní zážitek kde si příběhy píšeš sám.
                    </motion.p>
                    <div className="mt-8 flex items-center justify-center gap-4">
                        <Link to={"/whitelist"}>
                            <GlowButton>
                                <Sparkles className="h-5 w-5" />
                                Připojit se
                            </GlowButton>
                        </Link>
                    </div>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
                        <Stat k="Online" v={loading ? "…" : stats.online ? "ONLINE" : "OFFLINE"} />
                        <Stat k="Players" v={loading ? "…" : stats.players ? stats.players : 0} />
                        <Stat k="Join" v={loading ? "…" : (stats.join || "ERROR")} />
                    </div>
                </div>

                <div id="showcase" className="mt-12">
                    <ShardGallery />
                </div>

                <Marquee />

                <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {[
                        { title: "Reálné prostředí", text: "Město které dýchá životem. NPC, provoz a hudba dávají rytmus každé scéně." },
                        { title: "Custom frakce", text: "EMS, LSSD, novináři, podniky a zábava. Role pro každého a jasná pravidla." },
                        { title: "Síla enginu", text: "Stabilní servery a čisté skripty. Plynulý obraz a minimum lagů." },
                        { title: "Eventy", text: "Plánované i náhlé. Od závodů po příběhové linky s odměnami." },
                        { title: "Ekonomika", text: "Vyvážená, s místem pro začátečníky i veterány. Obchody, práce, podnikání." },
                        { title: "Komunita", text: "Spravedlivá administrativa, bezpečí na chatu a rychlá pomoc na Discordu." },
                    ].map((c, i)=> (
                        <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i*.08 }} className="relative rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
                            <div className="absolute -inset-px rounded-3xl bg-gradient-to-tr from-fuchsia-500/10 via-purple-500/10 to-indigo-500/10" />
                            <div className="relative">
                                <div className="text-sm uppercase tracking-widest text-white/60">{c.title}</div>
                                <div className="mt-2 text-base leading-relaxed text-white/75">{c.text}</div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <div className="mt-20">
                    <div className="mx-auto max-w-3xl text-center">
                        <motion.div initial={{ opacity: 0, scale: .96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: .6 }} className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 px-6 py-10 backdrop-blur">
                            <div className="absolute -inset-1 bg-[conic-gradient(from_180deg_at_50%_50%,rgba(168,85,247,.12),rgba(59,130,246,.12),rgba(16,185,129,.12),rgba(168,85,247,.12))] animate-[spin_12s_linear_infinite]" />
                            <div className="relative">
                                <div className="text-2xl font-bold">Připraveno na příběh</div>
                                <div className="mt-2 text-white/70">Nové postavy, nové linky a čistý start. Připoj se a ukážeme ti cestu do města.</div>
                                <div className="mt-6">
                                    <Link to={"/whitelist"}>
                                        <GlowButton>
                                            <Stars className="h-5 w-5" />
                                            Začít
                                        </GlowButton>
                                    </Link>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            <style>{`
        @keyframes pulse { 0%,100%{opacity:.3} 50%{opacity:.6}}
        @keyframes spin { to { transform: rotate(360deg) }}
      `}</style>
        </section>
    )
}
